import { JSX, useState, useCallback, FC, ChangeEvent } from "react";
import { Box, Flex, SxStyleProp } from "@otrium/core";
import { SearchInputIcon } from "src/icons/SearchInputIcon";
import { useTheme, Theme } from "@emotion/react";
import { CloseButton } from "src/atoms/CloseButton";
import { FormInput } from "@otrium/atoms";

interface Props {
  placeholder: string;
  onClearSearch?: () => void;
  handleChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

const InputTextSearch: FC<Props> = ({
  placeholder,
  handleChange,
  onClearSearch,
}): JSX.Element => {
  const [searchTerm, setSearchTerm] = useState("");
  const theme: Theme = useTheme();
  const searchIconStyle: SxStyleProp = {
    position: "absolute",
    top: 0,
    height: "40px",
    width: "40px",
  };

  if (onClearSearch) {
    searchIconStyle.left = 0;
  } else {
    searchIconStyle.right = 0;
  }

  const onType = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value);
      handleChange(event);
    },
    [searchTerm, handleChange]
  );

  const onClearSearchTerm = useCallback(() => {
    setSearchTerm("");
    if (onClearSearch) {
      onClearSearch();
    }
  }, [onClearSearch]);

  return (
    <Box sx={{ position: "relative" }}>
      <Flex alignItems="center" justifyContent="center" sx={searchIconStyle}>
        <SearchInputIcon height={"20px"} width={"20px"} stroke="#000" />
      </Flex>
      {onClearSearch && searchTerm.length > 0 && (
        <CloseButton
          onClick={onClearSearchTerm}
          sx={{
            position: "absolute",
            right: 0,
            top: 0,
            height: "40px",
            width: "40px",
            justifyContent: "center",
          }}
        />
      )}
      <FormInput
        p={1}
        pl={onClearSearch ? 5 : 2}
        pr={onClearSearch ? 0 : 5}
        mr={2}
        height={"40px"}
        width={"100%"}
        onChange={onType}
        placeholder={placeholder}
        value={searchTerm}
        color={theme.colors.tone.black}
        sx={{
          border: `1px solid ${theme.colors.tone.silver}`,
          "::placeholder": { color: theme.colors.tone.anthraciteLight },
        }}
      />
    </Box>
  );
};

export default InputTextSearch;
